import { type PropsWithChildren, type ReactNode, useRef, useState } from 'react';
import { type SwipeCallback, type TapCallback, useSwipeable } from 'react-swipeable';
import { useClickAnyWhere, useOnClickOutside } from 'usehooks-ts';
import cn from '@/utils/merge-class-names';
export interface SwipeableAction {
  key: string;
  placement: 'start' | 'end';
  icon: ReactNode;
  label: string;
  className?: string;
  onClick?: () => void;
}

interface Props {
  className?: string;
  actions: SwipeableAction[];
  onOpen?: (direction: 'start' | 'end') => void;
  onClose?: (direction: 'start' | 'end') => void;
}

export default function SwipeableActions({ className, actions, onOpen, onClose, children }: PropsWithChildren<Props>) {
  const [isScrolling, setIsScrolling] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<'start' | 'end' | null>(null);

  const wrapperRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(
    wrapperRef,
    () => {
      if (isExpanded !== null) {
        onClose?.(isExpanded);
        setIsExpanded(null);
      }
    },
    'touchstart',
  );

  const handlePanStart: SwipeCallback = (e) => {
    if (e.dir === 'Down' || e.dir === 'Up') setIsScrolling(true);
  };

  const handlePanEnd: SwipeCallback = () => {
    setIsScrolling(false);
  };

  const handleTap: TapCallback = ({ event: e }) => {
    if (isExpanded !== null) {
      e.preventDefault();
      e.stopPropagation();
      onClose?.(isExpanded);
      setIsExpanded(null);
    }
  };

  const handleSwipe: SwipeCallback = (e) => {
    if (isScrolling) return;
    if (isExpanded === null) {
      if (e.dir === 'Left') {
        setIsExpanded('end');
        onOpen?.('end');
      } else if (e.dir === 'Right') {
        setIsExpanded('start');
        onOpen?.('start');
      }
    } else if (isExpanded === 'start') {
      if (e.dir === 'Left') {
        setIsExpanded(null);
        onClose?.('start');
      }
    } else if (isExpanded === 'end') {
      if (e.dir === 'Right') {
        setIsExpanded(null);
        onClose?.('end');
      }
    }
  };

  const handlers = useSwipeable({
    trackMouse: true,
    trackTouch: true,
    touchEventOptions: { passive: false },
    onTap: handleTap,
    onSwiped: handlePanEnd,
    onSwipeStart: handlePanStart,
    onSwiping: handleSwipe,
  });

  return (
    <div
      ref={wrapperRef}
      className={cn('grid touch-pan-y grid-cols-2 grid-rows-1 overflow-x-hidden', className)}>
      <div
        className={cn('col-start-1 row-start-1 scale-100 transition-transform origin-right', {
          'scale-90': isExpanded !== 'start',
        })}>
        {actions
          .filter((action) => action.placement === 'start')
          .map((action, i) => (
            <button
              key={`action-start-${i}`}
              className={cn('btn btn-ghost h-full rounded-none', action.className)}
              type='button'
              onClick={() => {
                action.onClick?.();
                setIsExpanded(null);
                onClose?.('start');
              }}>
              {action.icon}
            </button>
          ))}
      </div>
      <div
        className={cn('z-10 col-span-full col-start-1 row-start-1 bg-neutral-0 transition-transform', {
          '*:pointer-events-none': isExpanded !== null,
        })}
        style={{
          transform:
            isExpanded === 'start' ? 'translateX(108px)' : isExpanded === 'end' ? 'translateX(-108px)' : undefined,
        }}
        {...handlers}>
        {children}
      </div>
      <div
        className={cn('col-end-3 row-start-1 scale-100 text-right transition-transform origin-left', {
          'scale-90': isExpanded !== 'end',
        })}>
        {actions
          .filter((action) => action.placement === 'end')
          .map((action, i) => (
            <button
              key={`action-end-${i}`}
              className={cn('btn btn-ghost h-full rounded-none', action.className)}
              type='button'
              onClick={() => {
                action.onClick?.();
                setIsExpanded(null);
                onClose?.('end');
              }}>
              {action.icon}
            </button>
          ))}
      </div>
    </div>
  );
}
